import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { useTheme } from 'components/core/theme-provider';
import ConfigurationLayout from 'components/configuration/ConfigurationLayout';
import { Dialog } from 'components/ui/dialog';
import { BuyCreditsDialog } from 'features/credits/components/buy-credits-dialog';
import { AddUser } from 'features/iam/components/add-user/add-user';
import { useCreditSummary } from 'features/credits/hooks/use-credits';
import { BuySeatsDialog } from 'features/iam/components/seats/buy-seats-dialog';
import { useGetUsersQuery } from 'features/iam/hooks/use-iam';
import { usePlanSeats } from 'features/subscription/hooks/use-seats';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { TeamsPeopleTab } from './components/teams-people-tab';
import { TeamsRequestsTab } from './components/teams-requests-tab';
import { TeamsUsageTab } from './components/teams-usage-tab';
import { teamsOutlinePillClassName, teamsPrimaryPillClassName } from './components/teams-styles';
import { TEAMS_REQUESTS_COUNT } from './teams-mock-data';

type TeamsTabId = 'people' | 'usage' | 'requests';

// Tab counts and the Requests tab are hidden until they are ready. Set to true to show them.
const notification_flag = false;

// Active tab lives in the URL (?tab=people|usage|requests); People is the default.
const getTabFromSearchParams = (searchParams: URLSearchParams): TeamsTabId => {
  const tab = searchParams.get('tab');
  if (tab === 'usage') return 'usage';
  if (tab === 'requests' && notification_flag) return 'requests';
  return 'people';
};

export function TeamsPage() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isAdmin = useCommonStore((state) => state.isAdmin);
  const isOrgAdmin = useCommonStore((state) => state.isOrgAdmin);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = getTabFromSearchParams(searchParams);
  const setActiveTab = (tab: TeamsTabId) => setSearchParams({ tab });
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [buySeatsOpen, setBuySeatsOpen] = useState(false);
  const { planSeats } = usePlanSeats(true);
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false);
  const { data: creditSummary } = useCreditSummary();
  const canBuyCredits = !!creditSummary?.enabled && creditSummary.canTopUp;

  const { data: peopleMeta } = useGetUsersQuery({
    page: 0,
    pageSize: 1,
    enabled: isAdmin || isOrgAdmin,
  });
  const peopleCount = peopleMeta?.totalCount;

  const tabs = useMemo(
    () =>
      [
        {
          id: 'people' as const,
          labelKey: 'TEAMS_TAB_PEOPLE',
          defaultLabel: 'People',
          count: peopleCount,
        },
        { id: 'usage' as const, labelKey: 'TEAMS_TAB_USAGE', defaultLabel: 'Usage' },
        {
          id: 'requests' as const,
          labelKey: 'TEAMS_TAB_REQUESTS',
          defaultLabel: 'Requests',
          count: TEAMS_REQUESTS_COUNT,
        },
      ]
        .filter((tab) => notification_flag || tab.id !== 'requests')
        .map((tab) => (notification_flag ? tab : { ...tab, count: undefined })) satisfies Array<{
        id: TeamsTabId;
        labelKey: string;
        defaultLabel: string;
        count?: number;
      }>,
    [peopleCount]
  );

  const titleColor = theme === 'dark' ? 'text-true-white' : 'text-grey-950';
  const subtitleColor = theme === 'dark' ? 'text-grey-300' : 'text-grey-500';
  const actionTextColor = theme === 'dark' ? 'text-true-white' : 'text-grey-900';
  const inactiveTabColor =
    theme === 'dark' ? 'text-grey-300 hover:text-true-white' : 'text-grey-600 hover:text-grey-950';
  const activeTabColor = theme === 'dark' ? 'text-true-white' : 'text-grey-950';
  const borderColor = theme === 'dark' ? 'border-border' : 'border-grey-100';
  const showPeopleActions = activeTab === 'people';

  return (
    <ConfigurationLayout className="gap-6 pb-8">
      <div className="flex min-w-0 flex-col gap-4 md:flex-row md:items-start md:justify-between">
        {/* Figma 10631:11663 — 4px stack; subtitle (10631:11665) stays one line from md. */}
        <div className="flex min-w-0 shrink-0 flex-col gap-1">
          <h1 className={cn('text-2xl font-medium leading-heading', titleColor)}>
            {t('TEAMS', { defaultValue: 'Teams' })}
          </h1>
          <p
            className={cn(
              'text-sm font-normal leading-body md:whitespace-nowrap',
              subtitleColor
            )}
          >
            {t('TEAMS_PAGE_SUBTITLE', {
              defaultValue: "Manage who's in your workspace, their plans and their credits.",
            })}
          </p>
        </div>

        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {!showPeopleActions && (
            <button
              type="button"
              className={cn(
                teamsOutlinePillClassName,
                actionTextColor,
                borderColor,
                'disabled:cursor-not-allowed disabled:opacity-50'
              )}
              disabled={!canBuyCredits}
              onClick={() => setBuyCreditsOpen(true)}
            >
              {t('TEAMS_BUY_CREDITS', { defaultValue: 'Buy credits' })}
            </button>
          )}
          <button
            type="button"
            className={cn(teamsOutlinePillClassName, actionTextColor, borderColor)}
            onClick={() => setBuySeatsOpen(true)}
          >
            {t('TEAMS_BUY_SEATS', { defaultValue: 'Buy seats' })}
          </button>
          {showPeopleActions && (
            <button
              type="button"
              className={teamsPrimaryPillClassName}
              onClick={() => setAddUserOpen(true)}
            >
              <Plus width={16} height={16} strokeWidth={1.5} className="shrink-0" />
              {t('TEAMS_ADD_USER', { defaultValue: 'Add user' })}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <div
          role="tablist"
          className={cn(
            'flex gap-7 overflow-x-auto overflow-y-hidden border-b',
            borderColor
          )}
        >
          {tabs.map((tab) => {
            const isActive = tab.id === activeTab;
            const countIsBadge = tab.id === 'requests' && tab.count != null;

            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex h-11 shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 text-sm',
                  isActive
                    ? cn('border-grey-950 font-medium', activeTabColor)
                    : cn('border-transparent font-normal', inactiveTabColor)
                )}
              >
                <span>{t(tab.labelKey, { defaultValue: tab.defaultLabel })}</span>
                {tab.count != null && !countIsBadge && (
                  <span
                    className={cn(
                      'text-xs leading-heading',
                      theme === 'dark' ? 'text-grey-400' : 'text-grey-500'
                    )}
                  >
                    {tab.count}
                  </span>
                )}
                {countIsBadge && (
                  <span className="inline-flex size-5 items-center justify-center rounded-full bg-grey-900 text-xs font-medium leading-heading text-true-white">
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {activeTab === 'usage' && <TeamsUsageTab />}
        {activeTab === 'people' && <TeamsPeopleTab />}
        {notification_flag && activeTab === 'requests' && <TeamsRequestsTab />}
      </div>

      {/* Mounted only while open, so each invite starts from an empty form. */}
      <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
        {addUserOpen && <AddUser onClose={() => setAddUserOpen(false)} />}
      </Dialog>
      <BuySeatsDialog
        open={buySeatsOpen}
        planSeats={planSeats}
        onClose={() => setBuySeatsOpen(false)}
      />
      {/* Mounted only while open, so each purchase starts from the first pack. */}
      {buyCreditsOpen && creditSummary && (
        <BuyCreditsDialog summary={creditSummary} onClose={() => setBuyCreditsOpen(false)} />
      )}
    </ConfigurationLayout>
  );
}
